import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PostGetProviderSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { SearchMockService } from '../../../shared/services/search-mock.service';
import { NeedHelpDecidingComponent} from '../../../../get-care-now-feature/mobile/components/need-help-deciding/need-help-deciding.component';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { ProviderSearchFilters } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';

@Component({
  selector: 'app-provider-search-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatSlideToggleModule, MatIconModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, NeedHelpDecidingComponent, MatProgressSpinner],
  templateUrl: './provider-search.component.html',
  styleUrl: './provider-search.component.css'
})
export class ProviderSearchComponent {
  mcAppGateway = inject(MCAppGatewayService);
  searchState = inject(SearchStateService);
  // mockService = inject(SearchMockService);

  public title: string = 'Find a Provider';
  public searchByText: string = 'Search by name or specialty';
  public namePlaceholderText: string = 'Provider name or specialty';
  public cityZipPlaceholderText: string = 'City or zip code (required)*'
  public fieldRequiredText: string = 'Location information required*';
  public searchBtnText: string = 'Search';
  public quickSearchTitleText: string = 'Quick searches:';
  public acceptsMyInsurance: string = 'Accepts My Insurance';
  public showSpinner: boolean = false;
  public useInsuranceFilter: boolean = false;
  public insuranceName: string | null = null;

  @Input() locale: string = '';
  @Input() isPlus: boolean | null = null;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() providerQuickSearchOptions: any[] = [];
  @Input() providerSearchFilters: ProviderSearchFilters | null = null;
  @Input() patientAffiliation: string | null = null;
  @Input() providerTypeAheadOptions: string[] = [];
  @Input() cityZipTypeAheadOptions: string[] = [];  

  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  showSpinner$ = this.searchState.showSpinner$;

  filteredProviderOptions!: Observable<string[]>;
  filteredCityZipOptions!: Observable<string[]>;

  sortMethod: "distance" | "availability" | "name" = "distance";
  providerSearchForm = new FormGroup({
    nameOrSpecialty: new FormControl('', Validators.required),
    cityOrZip: new FormControl('', Validators.required)
  });

  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
  }

  ngOnInit() {
    this.translate.use(sessionStorage.getItem('locale') || 'en-US');

    // Initialize autocomplete filtering
    this.filteredProviderOptions = this.providerSearchForm.get('nameOrSpecialty')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchValue = value || '';
        return searchValue.length < 3 ? [] : this._filterOptionsFuzzy(searchValue, this.providerTypeAheadOptions);
      })
    );

    this.filteredCityZipOptions = this.providerSearchForm.get('cityOrZip')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchValue = value || '';
        return searchValue.length < 3 ? [] : this._filterOptionsFuzzy(searchValue,  this.cityZipTypeAheadOptions);
      })
    );

    // Subscribe to search query to restore form values
    this.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.providerSearchForm.patchValue({
          nameOrSpecialty: query.nameOrSpecialty,
          cityOrZip: query.cityOrZip
        });
      }
    }); 

    this.searchState.providerSortMethod$.subscribe( sortMethod => {
      if (sortMethod) {
        this.sortMethod = sortMethod
      }
    });
    
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });
    
    // Load translations
    this.translate.get('FINDAPROVIDER').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('PROVIDER_SEARCH_BY').subscribe((translated: string) => {
      this.searchByText = translated;
    }); 

    this.translate.get('PROVIDER_NAME_PLACEHOLDER').subscribe((translated: string) => {
      this.namePlaceholderText = translated;
    });
    
    this.translate.get('PROVIDER_CITYZIP_PLACEHOLDER').subscribe((translated: string) => {
      this.cityZipPlaceholderText = translated;
    });

    this.translate.get('ACCEPTS_MY_INSURANCE').subscribe((translated: string) => {
      this.acceptsMyInsurance = translated;
    });


    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchBtnText = translated;
    });

    this.translate.get('QUICK_SEARCH_TITLE').subscribe((translated: string) => {  
      this.quickSearchTitleText = translated;
    });
  }

  onSubmit() {
    this.searchState.setShowSpinner(true);

    if (this.providerSearchForm.valid) {
      const formValues = this.providerSearchForm.value;

      if (formValues.nameOrSpecialty && formValues.cityOrZip) {
        var request = new PostGetProviderSearchRequest();
        request.nameOrSpecialty = formValues.nameOrSpecialty;
        request.cityOrZip = formValues.cityOrZip;

        if (this.currentLocation != null) {
          request.latitude = this.currentLocation?.latitude;
          request.longitude = this.currentLocation?.longitude;
        }

        if (this.patientAffiliation != null && this.patientAffiliation != '') {
          request.affiliations = this.patientAffiliation;
        }

        sessionStorage.setItem('providerSearchMethod', 'freeText');
        console.log('Form Submitted!', formValues, request);
        this.postGetProviderSearch(request);
      }
      else {
        alert('Please fill in all required fields before searching.');
        this.searchState.setShowSpinner(false);
      }
    }
    else {
      alert('Form is invalid. Please check the input fields.');
      console.log('Form is invalid', this.providerSearchForm.errors);
      this.searchState.setShowSpinner(false);
    }
  }

  onQuickSearch(option: any) {
    console.log('Quick Search Clicked!', option);
    this.searchState.setShowSpinner(true);

    var cityOrZip = this.providerSearchForm.get('cityOrZip')?.value ?? null;

    if (!cityOrZip) {
      console.warn('City or Zip is required for Quick Search.');
      this.searchState.setShowSpinner(false);
      return;
    }

    var request = new PostGetProviderSearchRequest();
    request.specialties = option.specialtyList;
    request.quickSearch = option.quickSearch;
    request.cityOrZip = cityOrZip;

    if(this.patientAffiliation != null && this.patientAffiliation != '') {
      request.affiliations = this.patientAffiliation;
    }

    this.searchState.setProviderSearchQuery({
      nameOrSpecialty: '',
      cityOrZip: cityOrZip
    });

    if (this.currentLocation != null) {
      request.latitude = this.currentLocation?.latitude;
      request.longitude = this.currentLocation?.longitude;
    }

    sessionStorage.setItem('providerSearchMethod', 'quickSearch');
    console.log('Submitting Quick Search! ', request);
    this.postGetProviderSearch(request);
  }

  postGetProviderSearch(request: PostGetProviderSearchRequest) {
    
    this.mcAppGateway.PostGetProviderSearch(request).subscribe({
      next: (response) => {
        console.log('Provider Search Response:', response);
        this.providerSearchForm.reset();

        var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.sortMethod);
        this.searchState.setProviderSearchResults(sortedResponse);

        if (request.specialties) {
          this.providerSearchFilters?.specialtyList?.forEach(specialty => {
            if (request.specialties?.toLowerCase().includes(specialty.value.toLowerCase())) {
              specialty.isSelected = true;
            }
          });
        }

        if (request.affiliations) {
          this.providerSearchFilters?.affiliationList.forEach(affiliation => {
            if(request.affiliations?.toLowerCase().includes(affiliation.value.toLowerCase())) {
              affiliation.isSelected = true
            }
          });
        }

        if (request.cityOrZip != null && request.cityOrZip != '' && this.providerSearchFilters != null) {
          this.providerSearchFilters.location = request.cityOrZip;
        }

        // if name/specialty has exact match among specialty list,
        // add as a mat chip in the filters section
        if (request.nameOrSpecialty) {
          this.providerSearchFilters?.specialtyList?.forEach(specialty => {
            if (request.nameOrSpecialty?.toLowerCase() === specialty.value.toLowerCase()) {   
              specialty.isSelected = true;
            } 
          });
        }

        // Set the search query in state service for display purposes
        this.searchState.setProviderSearchQuery({
          nameOrSpecialty: request.nameOrSpecialty ?? '',
          cityOrZip: request.cityOrZip ?? '',
        });

        this.searchState.setShowSpinner(false);
      },
      error: (error) => {
        // Clear previous query & results
        this.searchState.setProviderSearchQuery({nameOrSpecialty: '', cityOrZip: ''});
        this.searchState.clearProviderSearchResults();

        console.error('Provider Search Error:', error);
        alert('An error occurred while searching. Please try again.');
        
        this.searchState.setShowSpinner(false);
      }
    });
  }

  private _fuzzyMatch(filterValue: string, option: string): boolean {

    // Remove spaces, special charaters, and convert to lowercase for comparison
    const normalizedFilter = filterValue.toLowerCase().replace(/\s/g, '');
    const normalizedOption = option.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9]/g, '');
    
    return normalizedOption.includes(normalizedFilter)
  }

  private _filterOptionsFuzzy(value: string, options: string[]): string[] {
    if (!value) return options;
    
    return options.filter(option => 
      this._fuzzyMatch(value, option) || option.toLowerCase().includes(value.toLowerCase())
    );
  }
}

